import {
  Component,
  Input,
  OnInit,
  ViewChild,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  AfterViewInit,
  ElementRef,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { PackageService } from '../../services/package.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableDataSource } from '@angular/material/table';
import { ProfileService } from '../../services/profile.service';
import { TableUtils, DragScrollConfig } from '../../orders/utils/table.utils';
import {
  PackageRowActionItem,
  PackageRowActionsComponent,
} from '../../shared/components/package-row-actions/package-row-actions.component';
import { buildPackageFilterParams } from '../../shared/utils/package-filter-params.util';
import { OrderUtils } from '../../orders/utils/order.utils';

interface Package {
  id: string;
  package_name: string;
  /** Package category from API (`add_on` = add-on product). */
  type?: string;
  wash_types: string[];
  features: string[];
  package_status: boolean;
  family_eligible?: boolean;
  license_plate_needed: boolean;
  theme_color: string;
  single_list_price: string;
  single_strikeoff_price: string;
  monthly_list_price: string;
  monthly_strikeoff_price: string;
  wash_count_period: string;
  wash_count_frequency: number;
  status: boolean;
  created_at: string;
  updated_at: string;
  locations: { id: string; name: string }[];
  locations_str?: string;
  locations_tooltip?: string;
  features_tooltip?: string;
  status_str?: string;
  /** Add-on variant rows; prices often live here instead of root `single_list_price`. */
  variants?: { single_list_price?: string | null; single_strikeoff_price?: string | null }[];
}

@Component({
  selector: 'app-package-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatSelectModule,
    FormsModule,
    MatButtonModule,
    MatInputModule,
    MatTooltipModule,
    MatCheckboxModule,
    PackageRowActionsComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './package-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./package-table.component.scss'],
})
export class PackageTableComponent implements OnInit, AfterViewInit {
  /**
   * MatSort lives inside `*ngIf="!isLoading"`, so it may appear after the first
   * `ngAfterViewInit`. Re-attach whenever the directive becomes available.
   */
  private _sort: MatSort | null = null;
  @ViewChild(MatSort)
  set sort(sort: MatSort | undefined) {
    this._sort = sort ?? null;
    this.attachSort();
  }
  get sort(): MatSort | null {
    return this._sort;
  }

  private _tableScrollWrapper?: ElementRef<HTMLElement>;
  @ViewChild('tableScrollWrapper') set tableScrollWrapper(element: ElementRef<HTMLElement>) {
    if (element) {
      this._tableScrollWrapper = element;
      this.initializeDragScrolling();
    }
  }

  private dragScrollConfig!: DragScrollConfig;

  @Output() edit = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();
  @Output() duplicate = new EventEmitter<any>();
  @Output() tableFiltering = new EventEmitter<boolean>();

  /**
   * Filtered packages from the parent (`PackageComponent`). Parent owns search /
   * filter state; the table must not rebind unfiltered `PackageService.packages$`.
   */
  private _packages: Package[] = [];

  /** Rows per page for skeleton placeholders (declared before packages setter). */
  rows = 5;

  /**
   * Material table data source. Rebuilt when parent search/filter results change so
   * mat-table always reconnects to the filtered list (same source Grid uses).
   */
  dataSource = new MatTableDataSource<Package>([]);

  @Input()
  set packages(value: Package[]) {
    this.syncPackagesFromParent(value);
  }

  get packages(): Package[] {
    return this._packages;
  }

  // External loading input (optional). If not provided, internal loading is used
  @Input() loading: boolean | null = null;
  private internalLoading = false;
  // Skeleton placeholder rows
  skeletonRows: { __skeleton: true; id: number }[] = Array.from({ length: 10 }, (_, i) => ({
    __skeleton: true,
    id: i,
  }));
  washTypeOptions: any[] = [];
  statusOptions: any[] = [];
  locationOptions: any[] = [];
  displayWashTypeFilter = false;
  selectedWashTypes: string[] = [];
  selectedStatus: string[] = [];
  selectedLocations: string[] = [];

  // Filter panel visibility states
  isWashTypeFilterOpen = false;
  isStatusFilterOpen = false;
  isLocationFilterOpen = false;

  // Custom sort properties
  currentSortField = '';
  currentSortOrder: 'asc' | 'desc' | '' = '';

  displayedColumns: string[] = [
    'package_name',
    'wash_types',
    'wash_count',
    'package_status',
    'locations',
    'single_list_price',
    'single_strikeoff_price',
    'monthly_list_price',
    'monthly_strikeoff_price',
    'family_eligible',
    'license_plate_needed',
    'updated_at',
    'actions',
  ];

  readonly rowActionItems: PackageRowActionItem[] = [
    {
      id: 'edit',
      label: 'Edit',
      icon: 'assets/images/package/edit.svg',
      iconSize: 11,
    },
    {
      id: 'duplicate',
      label: 'Duplicate',
      icon: 'assets/images/package/add.svg',
      borderBoth: true,
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: 'assets/images/package/trash.svg',
      danger: true,
    },
  ];

  constructor(
    private packageService: PackageService,
    private profileService: ProfileService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Parent `PackageComponent` owns the list fetch and passes filtered packages via `@Input`.
    this.loadLocations();
  }

  /**
   * Apply the parent's filtered package list to the Material table.
   * Rebuilds `MatTableDataSource` so mat-table reconnects after search/filter changes
   * (in-place `data` updates can stall once MatSort is wired through `*ngIf`).
   */
  private syncPackagesFromParent(value: Package[] | null | undefined): void {
    this._packages = value ?? [];
    this.dataSource = new MatTableDataSource<Package>([...this._packages]);
    this.attachSort();
    this.populateFilters(this._packages);
    this.internalLoading = false;
    this.skeletonRows = Array.from({ length: this.rows }, (_, i) => ({
      __skeleton: true,
      id: i,
    }));
    this.cdr.markForCheck();
  }

  private attachSort(): void {
    // Only attach a real MatSort (has initialized stream). Tests / partial stubs
    // must not be wired into MatTableDataSource's merge() pipeline.
    if (this._sort?.initialized) {
      this.dataSource.sort = this._sort;
    }
  }

  currentPackage: any = null;

  setCurrentPackage(packageItem: any): void {
    this.currentPackage = packageItem;
  }

  onEditPackage(): void {
    if (this.currentPackage) {
      this.edit.emit(this.currentPackage);
    }
  }

  onDuplicatePackage(): void {
    if (this.currentPackage) {
      this.duplicate.emit(this.currentPackage);
    }
  }

  onDeletePackage(): void {
    if (this.currentPackage) {
      this.delete.emit(this.currentPackage);
    }
  }

  onRowAction(actionId: string): void {
    if (actionId === 'edit') {
      this.onEditPackage();
      return;
    }
    if (actionId === 'duplicate') {
      this.onDuplicatePackage();
      return;
    }
    if (actionId === 'delete') {
      this.onDeletePackage();
    }
  }

  showActionMenu(event: Event, packageItem: any): void {
    event.preventDefault();
    event.stopPropagation();
    this.setCurrentPackage(packageItem);
  }

  loadPackages(): void {
    this.internalLoading = true;
    // Prepare skeleton rows based on rows per page
    this.skeletonRows = Array.from({ length: this.rows }, (_, i) => ({ __skeleton: true, id: i }));
    const params = this.buildParams();

    // Check if we have any filters applied
    const hasFilters =
      this.selectedWashTypes.length > 0 ||
      this.selectedStatus.length > 0 ||
      this.selectedLocations.length > 0;

    // Notify parent component about filtering state
    this.tableFiltering.emit(hasFilters);

    this.packageService.loadPackages(params);
  }

  buildParams(): any {
    return buildPackageFilterParams({
      statuses: this.selectedStatus,
      carWashTypes: this.selectedWashTypes,
      locations: this.selectedLocations,
    });
  }

  populateFilters(packages: Package[]): void {
    // this.washTypeOptions = [...new Set(packages.flatMap(p => p.wash_types))].map(wt => ({ label: wt, value: wt }));
    this.washTypeOptions = [
      { label: 'Single Wash', value: 'single wash' },
      { label: 'Monthly Membership', value: 'monthly membership' },
      { label: 'Detailing', value: 'detailing' },
    ];
    this.statusOptions = [
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ];
  }

  loadLocations(): void {
    this.profileService.locations$.subscribe(locations => {
      this.locationOptions = locations?.map((loc: any) => ({ label: loc.branch, value: loc.id }));
    });
    // Initialize locations load
    this.profileService.loadLocations();
  }

  ngAfterViewInit(): void {
    this.attachSort();
    this.initializeDragScrolling();
  }

  private initializeDragScrolling(): void {
    if (!this._tableScrollWrapper?.nativeElement) return;

    // Cleanup and re-initialize using TableUtils
    const config = TableUtils.reinitializeDragScrolling(this._tableScrollWrapper);
    if (config) {
      this.dragScrollConfig = config;
    }
  }

  onFilterChange(): void {
    this.loadPackages();
  }

  onGlobalFilter(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    // Filter through datasource for Material table
    this.dataSource.filter = value.trim().toLowerCase();
  }

  toggleWashTypeFilter(event: Event) {
    this.isWashTypeFilterOpen = !this.isWashTypeFilterOpen;
    // Popover state is managed by Material and component state
  }

  applyWashTypeFilter() {
    this.loadPackages();
    this.isWashTypeFilterOpen = false;
  }

  clearWashTypeFilter() {
    this.selectedWashTypes = [];
    this.loadPackages();
    this.isWashTypeFilterOpen = false;
  }

  toggleStatusFilter(event: Event) {
    this.isStatusFilterOpen = !this.isStatusFilterOpen;
    // Popover state is managed by Material and component state
  }

  applyStatusFilter() {
    this.loadPackages();
    this.isStatusFilterOpen = false;
  }

  clearStatusFilter() {
    this.selectedStatus = [];
    this.loadPackages();
    this.isStatusFilterOpen = false;
  }

  // Add this method to check if status filter has values
  get hasStatusFilterValues(): boolean {
    return this.selectedStatus.length > 0;
  }

  toggleLocationFilter(event: Event) {
    this.isLocationFilterOpen = !this.isLocationFilterOpen;
    // Popover state is managed by Material and component state
  }

  applyLocationFilter() {
    this.loadPackages();
    this.isLocationFilterOpen = false;
  }

  clearLocationFilter() {
    this.selectedLocations = [];
    this.loadPackages();
    this.isLocationFilterOpen = false;
  }

  // Add these getters for the other filters
  get hasWashTypeFilterValues(): boolean {
    return this.selectedWashTypes.length > 0;
  }

  get hasLocationFilterValues(): boolean {
    return this.selectedLocations.length > 0;
  }

  /** Add-on row: show dedicated pill (not raw `add_on` from wash_types). */
  showAddOnTypePill(pkg: Package): boolean {
    return pkg.type === 'add_on' || (pkg.wash_types || []).includes('add_on');
  }

  /** Wash-type chips excluding `add_on` (handled by {@link showAddOnTypePill}). */
  washTypesForTypeColumn(pkg: Package): string[] {
    return (pkg.wash_types || []).filter(w => w !== 'add_on');
  }

  getWashTypeLabelForTable(wt: string): string {
    switch (wt) {
      case 'single_wash':
        return 'Single Wash';
      case 'monthly_membership':
        return 'Monthly Membership';
      case 'detailing':
        return 'Detailing';
      default:
        return wt;
    }
  }

  getWashCountLabelForTable(pkg: Package): string {
    return OrderUtils.formatPackageListWashCount(
      pkg?.wash_types,
      pkg?.wash_count_period,
      pkg?.wash_count_frequency
    );
  }

  /** Single-wash list/strike-off price; add-ons read from `variants[0]` when set. */
  displaySinglePrice(
    pkg: Package,
    field: 'single_list_price' | 'single_strikeoff_price'
  ): string | number {
    if (pkg?.type === 'add_on' && Array.isArray(pkg.variants) && pkg.variants.length) {
      const p = pkg.variants[0]?.[field];
      if (p != null && String(p).trim() !== '') {
        return p;
      }
    }
    return this.coerceCurrencyAmount(pkg?.[field]);
  }

  /** Currency pipe shows blank for null/'' — match prod $0.00 for empty amounts. */
  coerceCurrencyAmount(value: string | number | null | undefined): number {
    if (value == null || String(value).trim() === '') {
      return 0;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  // Get sort icon for a field
  /**
   * The header sort affordance is the same neutral icon in every state, matching the
   * packages design. Kept as a method (rather than a constant in the template) so a
   * per-state or per-column icon can be reintroduced without touching the markup.
   */
  getSortIcon(_field?: string): string {
    return 'assets/images/package/tabler_arrows-sort.svg';
  }

  // Custom sort function for PrimeNG table
  customSort(event: any) {
    // Update our tracking variables
    this.currentSortField = event.field || '';
    this.currentSortOrder = event.order === 1 ? 'asc' : event.order === -1 ? 'desc' : '';

    const numericFields = [
      'single_list_price',
      'single_strikeoff_price',
      'monthly_list_price',
      'monthly_strikeoff_price',
    ];

    // Perform the sorting
    event.data.sort((data1: any, data2: any) => {
      const value1 = this.getFieldValue(data1, event.field);
      const value2 = this.getFieldValue(data2, event.field);

      let result = null;

      if (value1 == null && value2 != null) {
        result = -1;
      } else if (value1 != null && value2 == null) {
        result = 1;
      } else if (value1 == null && value2 == null) {
        result = 0;
      } else if (numericFields.includes(event.field)) {
        // Handle numeric sorting for specified fields
        const num1 = parseFloat(value1);
        const num2 = parseFloat(value2);

        if (isNaN(num1) && isNaN(num2)) {
          result = 0;
        } else if (isNaN(num1)) {
          result = -1;
        } else if (isNaN(num2)) {
          result = 1;
        } else {
          result = num1 - num2;
        }
      } else if (typeof value1 === 'string' && typeof value2 === 'string') {
        // Handle string sorting
        result = value1.localeCompare(value2);
      } else {
        // Handle other types of sorting
        result = value1 < value2 ? -1 : value1 > value2 ? 1 : 0;
      }

      return event.order * result;
    });

    // Force change detection
    this.cdr.detectChanges();
  }

  // Helper method to get field value from object
  private getFieldValue(obj: any, field: string): any {
    if (field === 'single_list_price' || field === 'single_strikeoff_price') {
      return this.displaySinglePrice(obj, field);
    }
    return field.split('.').reduce((o, f) => o && o[f], obj);
  }

  // Unified loading getter
  get isLoading(): boolean {
    return (this.loading ?? this.internalLoading) === true;
  }

  // More reliable mobile/tablet detection
  get isMobileOrTablet(): boolean {
    // Check for user agent patterns
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;

    // Check screen size (common mobile/tablet breakpoints)
    const isSmallScreen = window.matchMedia('(max-width: 1024px)').matches;

    // Check for touch with screen size consideration
    const isTouchWithSmallScreen =
      ('ontouchstart' in window || navigator.maxTouchPoints > 0) && isSmallScreen;

    // Check for specific mobile/tablet indicators
    const hasMobileUA = mobileRegex.test(userAgent);

    // Combine checks for better accuracy
    return hasMobileUA || isTouchWithSmallScreen;
  }

  // For backward compatibility, keeping the old property name
  get isTouch(): boolean {
    return this.isMobileOrTablet;
  }
}
